import { MEETING_STATUS_BADGE_CONFIG } from "../constants/meetings";
import { EMeetingStatus } from "../types/meetings";

// NOTE: Single source for "which explanatory copy does this meeting status
// deserve?" (NSAI-4810 / NSAI-4811). Both the meeting-details banner and the
// Call Assistant list row need the same answer, and the two tickets are
// explicit that one change should add the missing explanation in both places —
// which only holds if the mapping lives in one spot rather than being spelled
// out per screen.
//
// The values are i18n keys in the `callAssistant` namespace, already present in
// i18n/locales/{en,ru,ar}/call-assistant.json. No new copy was introduced for
// this mapping; the detail screen simply never used the failure entries.
export type TMeetingStatusBannerKey =
  | "details.main_content.failed_banner"
  | "details.main_content.cancelled_banner"
  | "details.main_content.bot_not_admitted_banner"
  | "details.main_content.bot_lobby_timeout_banner"
  | "details.main_content.no_audio_track_banner"
  | "details.main_content.no_speech_detected_banner";

// NOTE: Keyed per status rather than derived from FAILED_MEETING_STATUSES so
// the four real failures can share one message while each non-failure terminal
// state keeps its own. A status absent from this map has nothing to explain —
// in-progress states are described by their badge, and PROCESSED is the happy
// path.
const MEETING_STATUS_BANNER_KEYS: Partial<
  Record<EMeetingStatus, TMeetingStatusBannerKey>
> = {
  [EMeetingStatus.RECORDING_FAILED]: "details.main_content.failed_banner",
  [EMeetingStatus.TRANSCRIPTION_FAILED]: "details.main_content.failed_banner",
  [EMeetingStatus.SUMMARIZATION_FAILED]: "details.main_content.failed_banner",
  [EMeetingStatus.FAILED]: "details.main_content.failed_banner",
  [EMeetingStatus.CANCELLED]: "details.main_content.cancelled_banner",
  [EMeetingStatus.BOT_NOT_ADMITTED]:
    "details.main_content.bot_not_admitted_banner",
  [EMeetingStatus.BOT_LOBBY_TIMEOUT]:
    "details.main_content.bot_lobby_timeout_banner",
  [EMeetingStatus.NO_AUDIO_TRACK]: "details.main_content.no_audio_track_banner",
  [EMeetingStatus.NO_SPEECH_DETECTED]:
    "details.main_content.no_speech_detected_banner",
};

// NOTE: Returns null when the status needs no explanation, so callers can
// render nothing without repeating the status list.
export const getMeetingStatusBannerKey = (
  status: EMeetingStatus,
): TMeetingStatusBannerKey | null => MEETING_STATUS_BANNER_KEYS[status] ?? null;

// NOTE: Derived from the badge config rather than restating the list, so a
// status can never get a red badge above a neutral explanation or vice versa.
// This used to be a second hand-written set and it had already drifted from the
// badge once.
export const hasDestructiveMeetingStatusBanner = (
  status: EMeetingStatus,
): boolean => MEETING_STATUS_BADGE_CONFIG[status].variant === "destructive";
